import { Dtos, Relations, UserId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { match } from "ts-pattern";
import { useRequest } from "../../../utils/request";

import {
	ActionItemByIdMap,
	adminActionItemsQueryKey,
	toRelation,
	transformActionItem,
} from "../action-item.keys";

import { ActionItem } from "../types";

export const useUpdateActionItem = () => {
	const queryClient = useQueryClient();
	const { patch } = useRequest();

	const mutation = useMutation({
		mutationFn: async ({
			actionItemId,
			updatedFields,
			relation,
		}: {
			actionItemId: Dtos.ActionItem.Update.Response["id"];
			updatedFields: Dtos.ActionItem.Update.Request;
			relation: Relations.ActionItems;
		}) => {
			return await patch<Dtos.ActionItem.Update.Response>(
				`/v2/surety/${relation.relationType}/${relation.relationId}/action-items/${actionItemId}`,
				updatedFields,
			);
		},
		onMutate: async ({
			actionItemId,
			updatedFields,
			relation,
		}: {
			actionItemId: Dtos.ActionItem.Update.Response["id"];
			updatedFields: Dtos.ActionItem.Update.Request;
			relation: Relations.ActionItems;
		}) => {
			await queryClient.cancelQueries({
				queryKey: adminActionItemsQueryKey.actionItemsByRelation(relation),
			});

			const previousActionItems = queryClient.getQueryData<ActionItemByIdMap>(
				adminActionItemsQueryKey.actionItemsByRelation(relation),
			);

			queryClient.setQueryData(
				adminActionItemsQueryKey.actionItemsByRelation(relation),
				(old: ActionItemByIdMap | undefined) => {
					if (old === undefined) return old;

					const existing = old[actionItemId];
					if (existing === undefined) return old;

					return {
						...old,
						[actionItemId]: transformActionItem({ ...existing, ...updatedFields }),
					};
				},
			);

			return { previousActionItems, relation };
		},
		onError: (_error, _variables, context) => {
			if (context === undefined) return;

			queryClient.setQueryData(
				adminActionItemsQueryKey.actionItemsByRelation(context.relation),
				context.previousActionItems,
			);
		},
		onSuccess: (result) => {
			if (result) {
				queryClient.invalidateQueries({ queryKey: adminActionItemsQueryKey.all(), exact: true });
			}
		},
	});

	const toggleCompleted = (actionItem: ActionItem, completedByUserId: UserId) => {
		const relation = toRelation(actionItem);

		match(actionItem)
			.with({ status: "open" }, (actionItem) => {
				mutation.mutate({
					actionItemId: actionItem.id,
					updatedFields: {
						completedAt: DateTime.now(),
						status: "complete",
						completedByUserId,
					},
					relation,
				});
			})
			.with({ status: "complete" }, (actionItem) => {
				mutation.mutate({
					actionItemId: actionItem.id,
					updatedFields: {
						completedAt: null,
						status: "open",
						completedByUserId: null,
					},
					relation,
				});
			})
			.exhaustive();
	};

	const updateDueDate = (actionItem: ActionItem, dueAt: DateTime) => {
		const relation = toRelation(actionItem);

		mutation.mutate({
			actionItemId: actionItem.id,
			updatedFields: {
				dueAt,
			},
			relation,
		});
	};

	const updateContent = (actionItem: ActionItem, content: string | null) => {
		const relation = toRelation(actionItem);

		mutation.mutate({
			actionItemId: actionItem.id,
			updatedFields: {
				content,
			},
			relation,
		});
	};

	const updateTitle = (actionItem: ActionItem, title: string) => {
		const relation = toRelation(actionItem);

		mutation.mutate({
			actionItemId: actionItem.id,
			updatedFields: {
				title,
			},
			relation,
		});
	};

	return {
		updateActionItem: mutation.mutate,
		toggleCompleted,
		updateContent,
		updateDueDate,
		updateTitle,
		updateActionItemLoading: mutation.isPending,
	};
};
